import { useState, useEffect } from 'react';
import { scrollToId } from '../utils/smoothScroll';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useTranslations } from '../context/TranslationContext';
import { InlineLanguageSwitch } from './LanguageDropDown';
import useAnalytics from '../hooks/useAnalytics';
import { ArrowLeft, X } from 'lucide-react';

interface HeaderProps {
  variant: 'home' | 'page';
}

type NavItem =
  | { id: string; label: string; type: 'anchor' }
  | { id: string; label: string; type: 'route'; path: string };

export const Header: React.FC<HeaderProps> = ({ variant }) => {
  const { t, lang } = useTranslations();
  const { trackEvent } = useAnalytics();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    if (variant !== 'home') return;
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [variant]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const navItems: NavItem[] = [
    { id: 'services', label: t.page.navigation.services, type: 'anchor' },
    { id: 'faq', label: t.page.navigation.faq, type: 'anchor' },
    { id: 'about', label: t.page.navigation.aboutme, type: 'anchor' },
    { id: 'contact', label: t.page.navigation.contact, type: 'anchor' },
    { id: 'articles', label: t.page.navigation.articles, type: 'route', path: '/articles' },
  ];

  const handleNavClick = (itemId: string, itemLabel: string) => {
    trackEvent('navigation_click', {
      clicked_item: itemId,
      clicked_label: itemLabel,
      language: lang,
      timestamp: new Date().toISOString(),
    });
    setMobileOpen(false);
  };

  const isActive = (item: NavItem) => {
    if (item.type === 'route') {
      return location.pathname.includes(item.path);
    }
    return false;
  };

  const headerBg =
    variant === 'page' || isScrolled
      ? 'bg-cream/95 backdrop-blur-md border-b border-hair'
      : 'bg-transparent';

  const homeUrl = lang === 'hu' ? '/hu' : `/${lang}`;

  // Sub-pages step up one level rather than always jumping home, so the trail
  // reads home → articles → article. Matches the BreadcrumbList we emit.
  const isArticleDetail = /^\/(?:ro|hu|en)\/articles\/[^/]+/.test(location.pathname);
  const backUrl = isArticleDetail ? `/${lang}/articles` : homeUrl;
  const backLabel = isArticleDetail
    ? t.page.navigation.backToArticles
    : t.page.navigation.back;

  const renderNavLink = (item: NavItem, className: string) => {
    if (item.type === 'route') {
      return (
        <RouterLink
          key={item.id}
          to={`/${lang}${item.path}`}
          className={`${className} ${isActive(item) ? 'text-accent' : ''}`}
          onClick={() => handleNavClick(item.id, item.label)}
        >
          {item.label}
        </RouterLink>
      );
    }
    if (variant === 'home') {
      return (
        <a
          key={item.id}
          href={`#${item.id}`}
          className={className}
          onClick={(e) => {
            e.preventDefault();
            handleNavClick(item.id, item.label);
            scrollToId(item.id);
            window.history.replaceState(null, '', `#${item.id}`);
          }}
        >
          {item.label}
        </a>
      );
    }
    return (
      <RouterLink
        key={item.id}
        to={`/${lang}#${item.id}`}
        className={className}
        onClick={() => handleNavClick(item.id, item.label)}
      >
        {item.label}
      </RouterLink>
    );
  };

  const anchorItems = navItems.filter(i => i.type === 'anchor');
  const routeItems = navItems.filter(i => i.type === 'route');

  return (
    <>
      <header className={`fixed w-full z-50 transition-all duration-500 ${headerBg}`}>
        <div className="max-w-7xl mx-auto px-8 lg:px-16 py-6 flex items-center justify-between">
          {variant === 'home' ? (
            <a href="#home" className="flex items-center gap-3">
              <Logo />
              <span className="text-[13px] tracking-[0.2em] uppercase font-medium">
                {t.page.title}
              </span>
            </a>
          ) : (
            <RouterLink to={homeUrl} className="flex items-center gap-3">
              <Logo />
              <span className="text-[13px] tracking-[0.2em] uppercase font-medium">
                {t.page.title}
              </span>
            </RouterLink>
          )}

          {variant === 'home' ? (
            <>
              <nav className="hidden md:flex items-center gap-10">
                {anchorItems.map((item) =>
                  renderNavLink(item, 'link-grow text-[12px] tracking-[0.18em] uppercase')
                )}
                <div className="flex items-center gap-10 pl-6 border-l border-hair">
                  {routeItems.map((item) =>
                    renderNavLink(item, 'link-grow text-[12px] tracking-[0.18em] uppercase')
                  )}
                </div>
                <div className="flex items-center gap-3 pl-6 border-l border-hair">
                  <InlineLanguageSwitch />
                </div>
              </nav>

              <button
                className="md:hidden p-2"
                onClick={() => setMobileOpen(true)}
                aria-label="Open menu"
              >
                <div className="flex flex-col gap-[5px]">
                  <span className="block w-5 h-px bg-ink" />
                  <span className="block w-5 h-px bg-ink" />
                </div>
              </button>
            </>
          ) : (
            <nav className="flex items-center gap-6">
              <RouterLink
                to={backUrl}
                className="flex items-center gap-2 text-[12px] tracking-[0.18em] uppercase text-mute hover:text-ink transition-colors"
                onClick={() => handleNavClick('back', backLabel)}
              >
                <ArrowLeft size={14} strokeWidth={1.5} />
                <span>{backLabel}</span>
              </RouterLink>
              <div className="flex items-center gap-3 pl-6 border-l border-hair">
                <InlineLanguageSwitch />
              </div>
            </nav>
          )}
        </div>
      </header>

      {mobileOpen && (
        <div className="fixed inset-0 z-[60] bg-cream flex flex-col animate-fadeIn">
          <div className="px-8 py-6 flex items-center justify-between">
            <a href="#home" className="flex items-center gap-3" onClick={() => setMobileOpen(false)}>
              <Logo />
            </a>
            <button onClick={() => setMobileOpen(false)} aria-label="Close menu" className="p-2">
              <X size={20} strokeWidth={1.5} />
            </button>
          </div>

          <nav className="flex-1 flex flex-col items-center justify-center gap-10">
            {navItems.map((item) =>
              renderNavLink(
                item,
                `font-serif font-light text-[34px] leading-none tracking-[-0.01em] transition-colors ${
                  isActive(item) ? 'text-accent' : 'text-ink hover:text-accent'
                }`
              )
            )}
          </nav>

          <div className="px-8 py-8 flex justify-center">
            <InlineLanguageSwitch />
          </div>
        </div>
      )}
    </>
  );
};

const Logo = () => (
  <div className="w-9 h-9 border border-ink flex items-center justify-center text-[22px] leading-none tracking-[0] font-medium font-serif italic">
    <span className="-translate-x-px -translate-y-px">KT</span>
  </div>
);
